// client/src/hooks/useAgendamentos.js (VERSÃO COMPLETA E CORRIGIDA)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import apiClient from '../services/api';
import useAlert from './useAlert';

const AGENDAMENTOS_QUERY_KEY = 'agendamentos';

// ======================================================
// 🎯 HOOK PRINCIPAL: Buscar agendamentos com filtros
// ======================================================
export const useAgendamentos = (params = {}, options = {}) => {
    // ✅ SÓ EXECUTA SE HOUVER UNIDADE (para evitar chamadas vazias)
    const isEnabled = options.enabled !== false && (params.unidade || params.participante || params.profissional);
    
    return useQuery({
        queryKey: [AGENDAMENTOS_QUERY_KEY, params],
        queryFn: async ({ signal }) => {
            const paramsComPopulate = {
                ...params,
                populate: 'financeiro.venda,itens.item,participante,profissional'
            };
            
            console.log('🚀 [useAgendamentos] Executando query:', paramsComPopulate);
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const { data } = await apiClient.get('/agendamentos', { 
                params: paramsComPopulate,
                signal 
            });
            return data;
        },
        placeholderData: (previousData) => previousData,
        staleTime: 1 * 60 * 1000,
        enabled: isEnabled,
        ...options
    });
};

// ======================================================
// 🎯 HOOK: Buscar agendamento específico
// ======================================================
export const useAgendamento = (agendamentoId, options = {}) => {
    return useQuery({
        queryKey: [AGENDAMENTOS_QUERY_KEY, agendamentoId],
        queryFn: async ({ signal }) => {
            if (!agendamentoId) return null;
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const { data } = await apiClient.get(`/agendamentos/${agendamentoId}`, {
                params: {
                    populate: 'financeiro.venda,itens.item,participante,profissional,recursosDependentes,pacoteCliente'
                },
                signal
            });
            
            return data.data;
        },
        enabled: !!agendamentoId && (options.enabled !== undefined ? options.enabled : true),
        staleTime: 1 * 60 * 1000,
        ...options
    });
};

// ======================================================
// 🎯 HOOK: Buscar venda específica de agendamento
// ======================================================
export const useVendaAgendamento = (vendaId) => {
    return useQuery({
        queryKey: ['venda', vendaId],
        queryFn: async ({ signal }) => {
            if (!vendaId) return null;
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const { data } = await apiClient.get(`/vendas/${vendaId}`, { signal });
            return data.data;
        },
        enabled: !!vendaId,
    });
};

// ======================================================
// 🎯 HOOK: Buscar agendamentos por profissional
// ======================================================
export const useAgendamentosPorProfissional = (profissionalId, params = {}) => {
    const isEnabled = !!profissionalId;
    
    return useQuery({
        queryKey: ['agendamentos-profissional', profissionalId, params],
        queryFn: async ({ signal }) => {
            if (!profissionalId) return { data: [] };
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const queryParams = {
                profissional: profissionalId,
                populate: 'itens.item,participante',
                ...params
            };
            
            const { data } = await apiClient.get('/agendamentos', { 
                params: queryParams,
                signal 
            });
            return data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK: Buscar agendamentos por cliente
// ======================================================
export const useAgendamentosPorCliente = (clienteId, params = {}) => {
    const isEnabled = !!clienteId;
    
    return useQuery({
        queryKey: ['agendamentos-cliente', clienteId, params],
        queryFn: async ({ signal }) => {
            if (!clienteId) return { data: [] };
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const queryParams = {
                participante: clienteId, 
                populate: 'itens.item,profissional',
                ...params
            };
            
            const { data } = await apiClient.get('/agendamentos', { 
                params: queryParams,
                signal 
            });
            return data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK: Buscar agendamentos por data
// ======================================================
export const useAgendamentosPorData = (dataAgendamento, params = {}) => {
    const isEnabled = !!dataAgendamento;
    
    return useQuery({
        queryKey: ['agendamentos-data', dataAgendamento, params],
        queryFn: async ({ signal }) => {
            if (!dataAgendamento) return { data: [] };
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const queryParams = {
                dataAgendamento,
                populate: 'itens.item,participante,profissional', 
                ...params
            };
            
            const { data } = await apiClient.get('/agendamentos', { 
                params: queryParams,
                signal 
            });
            return data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK: Buscar agendamentos por status
// ======================================================
export const useAgendamentosPorStatus = (status, params = {}) => {
    const isEnabled = !!status;
    
    return useQuery({
        queryKey: ['agendamentos-status', status, params],
        queryFn: async ({ signal }) => {
            if (!status) return { data: [] };
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const queryParams = {
                status,
                populate: 'itens.item,participante,profissional', 
                ...params
            };
            
            const { data } = await apiClient.get('/agendamentos', { 
                params: queryParams,
                signal 
            });
            return data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK: Buscar disponibilidade de profissionais
// ======================================================
export const useDisponibilidadeProfissional = (profissionalId, data, duracao) => {
    const isEnabled = !!profissionalId && !!data;
    
    return useQuery({
        queryKey: ['disponibilidade-profissional', profissionalId, data, duracao],
        queryFn: async ({ signal }) => {
            if (!profissionalId || !data) return { data: [] };
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const { data: response } = await apiClient.get(`/disponibilidade/profissional/${profissionalId}`, {
                params: { dataInicio: data, duracao },
                signal
            });
            
            return response.data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK: Buscar pacotes disponíveis para cliente
// ======================================================
export const usePacotesDisponiveisCliente = (clienteId, dataAgendamento) => {
    const isEnabled = !!clienteId;
    
    return useQuery({
        queryKey: ['pacotes-disponiveis', clienteId, dataAgendamento],
        queryFn: async ({ signal }) => {
            if (!clienteId) return { data: [] };
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const params = dataAgendamento ? { dataAgendamento } : {};
            const { data } = await apiClient.get(`/agendamentos/cliente/${clienteId}/pacotes-disponiveis`, { 
                params,
                signal 
            });
            
            return data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK: Verificar disponibilidade de pacote
// ======================================================
export const useVerificarPacoteDisponivel = () => {
    return useMutation({
        mutationFn: ({ pacoteClienteId, dataAgendamento, clienteId }) => {
            return apiClient.post('/agendamentos/verificar-pacote', {
                pacoteClienteId,
                dataAgendamento,
                clienteId
            });
        },
        onSuccess: (res) => {
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao verificar disponibilidade do pacote';
            throw new Error(errorMessage);
        },
    });
};

// ======================================================
// 🎯 HOOK: Obter fluxo completo do agendamento
// ======================================================
export const useFluxoCompletoAgendamento = (agendamentoId) => {
    const isEnabled = !!agendamentoId;
    
    return useQuery({
        queryKey: ['fluxo-completo-agendamento', agendamentoId],
        queryFn: async ({ signal }) => {
            if (!agendamentoId) return null;
            
            if (signal?.aborted) {
                throw new Error('Query cancelled');
            }
            
            const { data } = await apiClient.get(`/agendamentos/${agendamentoId}/fluxo-completo`, { signal });
            return data.data;
        },
        enabled: isEnabled,
    });
};

// ======================================================
// 🎯 HOOK PRINCIPAL: Mutações de agendamentos
// ======================================================
export const useMutateAgendamentos = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateAgendamentosCache = (agendamentoId = null) => {
        queryClient.invalidateQueries({ queryKey: [AGENDAMENTOS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: ['agendamentos-profissional'] });
        queryClient.invalidateQueries({ queryKey: ['agendamentos-cliente'] });
        queryClient.invalidateQueries({ queryKey: ['agendamentos-data'] });
        queryClient.invalidateQueries({ queryKey: ['agendamentos-status'] });
        queryClient.invalidateQueries({ queryKey: ['vendas'] });
        queryClient.invalidateQueries({ queryKey: ['pedidos'] });
        queryClient.invalidateQueries({ queryKey: ['pacotes-disponiveis'] });
        queryClient.invalidateQueries({ queryKey: ['fluxo-completo-agendamento'] });
        queryClient.invalidateQueries({ queryKey: ['disponibilidade'] });
        queryClient.invalidateQueries({ queryKey: ['disponibilidade-profissional'] });
        
        if (agendamentoId) {
            queryClient.invalidateQueries({ queryKey: [AGENDAMENTOS_QUERY_KEY, agendamentoId] });
            queryClient.invalidateQueries({ queryKey: ['fluxo-completo-agendamento', agendamentoId] });
        }
    };

    const verificarDisponibilidade = useMutation({
        mutationFn: (dadosAgendamento) => {
            console.log('🎯 [FRONTEND] Verificando disponibilidade:', dadosAgendamento);
            return apiClient.post('/disponibilidade/verificar-agendamento', dadosAgendamento);
        },
        onSuccess: (res) => {
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao verificar disponibilidade no sistema';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const verificarConflitos = useMutation({
        mutationFn: (conflitoData) => {
            console.log('🎯 [FRONTEND] Verificando conflitos:', conflitoData);
            return apiClient.post('/agendamentos/verificar-conflitos', conflitoData);
        },
        onSuccess: (res) => {
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || 'Erro ao verificar conflitos';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    // Contrato canônico do payload — ver bloco de documentação em
    // server/controllers/agendamentosController.js (CONTRATO CANÔNICO —
    // AgendamentoPayload). Transições de status (confirmar/concluir/cancelar,
    // abaixo) sempre usam os endpoints dedicados, nunca PUT genérico com
    // { status } — o genérico pula comunicação automática, liberação de
    // sessão de pacote e fechamento financeiro.
    const createAgendamento = useMutation({
        mutationFn: (agendamentoData) => {
            if (!agendamentoData.itens || agendamentoData.itens.length === 0) {
                throw new Error('Pelo menos um item/serviço deve ser selecionado');
            }
            
            const payload = {
                ...agendamentoData,
                metodoPagamento: agendamentoData.metodoPagamento || null,
                statusPagamento: agendamentoData.metodoPagamento ? 'Pago' : 'Pendente'
            };
            
            console.log('🎯 [FRONTEND] Criando agendamento:', payload);
            return apiClient.post('/agendamentos', payload);
        },
        onSuccess: (response) => {
            invalidateAgendamentosCache();
            const message = response.data?.message || 'Agendamento criado com sucesso!';
            setAlert(message, 'success');
            return response.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao criar agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const updateAgendamento = useMutation({
        mutationFn: ({ id, agendamentoData }) => {
            const payload = {
                ...agendamentoData,
                metodoPagamento: agendamentoData.metodoPagamento !== undefined ? agendamentoData.metodoPagamento : null,
                statusPagamento: agendamentoData.statusPagamento || 'Pendente'
            };
            
            console.log('🎯 [FRONTEND] Atualizando agendamento:', { id, payload });
            return apiClient.put(`/agendamentos/${id}`, payload);
        },
        onSuccess: (response) => {
            invalidateAgendamentosCache(response.data?.data?._id);
            const message = response.data?.message || 'Agendamento atualizado com sucesso!';
            setAlert(message, 'success');
            return response.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });
    
    const deleteAgendamento = useMutation({
        mutationFn: (id) => {
            console.log('🎯 [FRONTEND] Deletando agendamento:', id);
            return apiClient.delete(`/agendamentos/${id}`);
        },
        onSuccess: (response) => {
            invalidateAgendamentosCache();
            setAlert('Agendamento removido com sucesso!', 'success');
            return response.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao remover agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const confirmarAgendamento = useMutation({
        mutationFn: (id) => {
            console.log('🎯 [FRONTEND] Confirmando agendamento:', id);
            return apiClient.put(`/agendamentos/${id}/confirmar`);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            setAlert(res.data.message || 'Agendamento confirmado!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao confirmar agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });
    
    const cancelarAgendamento = useMutation({
        mutationFn: ({ id, motivo }) => {
            console.log('🎯 [FRONTEND] Cancelando agendamento:', { id, motivo });
            return apiClient.put(`/agendamentos/${id}/cancelar`, { motivo });
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            setAlert(res.data.message || 'Agendamento cancelado!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao cancelar agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const marcarNoShowAgendamento = useMutation({
        mutationFn: ({ id, motivo }) => {
            console.log('🎯 [FRONTEND] Marcando no-show:', { id, motivo });
            return apiClient.put(`/agendamentos/${id}/no-show`, { motivo });
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            setAlert(res.data.message || 'Agendamento marcado como no-show!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao marcar no-show';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const concluirAgendamento = useMutation({
        mutationFn: (id) => {
            console.log('🎯 [FRONTEND] Concluindo agendamento:', id);
            return apiClient.put(`/agendamentos/${id}/concluir`);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            queryClient.invalidateQueries({ queryKey: ['vendas'] });
            const message = res.data?.message || 'Agendamento concluído com sucesso!';
            setAlert(message, 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao concluir agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const checkoutAgendamento = useMutation({
        mutationFn: ({ id, checkoutData }) => {
            const payload = {
                metodoPagamentoId: checkoutData.metodoPagamentoId || null,
                statusPagamento: checkoutData.statusPagamento || 'Pago',
                ...checkoutData
            };
            
            console.log('🎯 [FRONTEND] Realizando checkout:', { id, payload });
            return apiClient.post(`/agendamentos/${id}/checkout`, payload);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            queryClient.invalidateQueries({ queryKey: ['vendas'] });
            setAlert(res.data.message || 'Checkout realizado com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao realizar checkout';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const atualizarVendaAgendamento = useMutation({
        mutationFn: ({ agendamentoId, vendaData }) => {
            console.log('🎯 [FRONTEND] Atualizando venda do agendamento:', { agendamentoId, vendaData });
            
            const payload = {
                statusVenda: vendaData.statusVenda,
                statusPagamento: vendaData.statusPagamento,
                observacoes: vendaData.observacoes
            };
            
            return apiClient.put(`/agendamentos/${agendamentoId}/venda`, payload);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            queryClient.invalidateQueries({ queryKey: ['vendas'] });
            setAlert('Venda do agendamento atualizada com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao atualizar venda';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const reagendarAgendamento = useMutation({
        mutationFn: ({ id, novaData, novoHorario, motivo }) => {
            console.log('🎯 [FRONTEND] Reagendando:', { id, novaData, novoHorario });
            
            const payload = {
                dataInicio: novaData,
                dataFim: novoHorario,
                observacoes: motivo
            };

            return apiClient.put(`/agendamentos/${id}`, payload);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            setAlert(res.data.message || 'Agendamento reagendado com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao reagendar agendamento';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const gerarPedidoAgendamento = useMutation({
        mutationFn: (agendamentoId) => {
            console.log('🛒 [FRONTEND] Gerando pedido para agendamento:', agendamentoId);
            return apiClient.post(`/agendamentos/${agendamentoId}/gerar-pedido`);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            queryClient.invalidateQueries({ queryKey: ['pedidos'] });
            setAlert('Pedido gerado a partir do agendamento com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao gerar pedido';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const gerarVendaPedido = useMutation({
        mutationFn: (agendamentoId) => {
            console.log('💰 [FRONTEND] Gerando venda para pedido do agendamento:', agendamentoId);
            return apiClient.post(`/agendamentos/${agendamentoId}/gerar-venda`);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache(res.data?.data?._id);
            queryClient.invalidateQueries({ queryKey: ['vendas'] });
            setAlert('Venda gerada a partir do pedido com sucesso!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao gerar venda';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const enviarMensagemAutomatica = useMutation({
        mutationFn: ({ id, evento }) => {
            console.log('🎯 [FRONTEND] Enviando mensagem automática:', { id, evento });
            return apiClient.post(`/agendamentos-comunicacao/${id}/enviar-mensagem-automatica`, { evento });
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache();
            queryClient.invalidateQueries({ queryKey: ['historicoComunicacao'] });
            setAlert(res.data.message || 'Mensagem automática enviada!', 'success');
            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar mensagem automática';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const enviarMensagemManual = useMutation({
        // ✅ periodoEnvio opcional — ausente = envio imediato de sempre;
        // presente (momento 'antes'/'depois') agenda em vez de enviar agora
        // (ver server/controllers/agendamentoComunicacaoController.js::enviarMensagemManual).
        mutationFn: ({ id, templateId, periodoEnvio }) => {
            console.log('🎯 [FRONTEND] Enviando mensagem manual:', { id, templateId, periodoEnvio });
            const body = periodoEnvio ? { templateId, periodoEnvio } : { templateId };
            return apiClient.post(`/agendamentos-comunicacao/${id}/enviar-mensagem-manual`, body);
        },
        onSuccess: (res) => {
            invalidateAgendamentosCache();
            queryClient.invalidateQueries({ queryKey: ['historicoComunicacao'] });
            queryClient.invalidateQueries({ queryKey: ['comunicacaoAgendada'] });
            setAlert(res.data.message || 'Mensagem manual enviada!', 'success');

            if (res.data.data?.urlWhatsApp) {
                window.open(res.data.data.urlWhatsApp, '_blank');
            }

            return res.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao enviar mensagem manual';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const obterUrlWhatsApp = useMutation({
        mutationFn: ({ id, mensagemPersonalizada, templateId }) => {
            console.log('🎯 [FRONTEND] Gerando URL WhatsApp:', { id, mensagemPersonalizada, templateId });
            return apiClient.get(`/agendamentos-comunicacao/${id}/url-whatsapp`, {
                params: { 
                    mensagemPersonalizada: mensagemPersonalizada,
                    templateId: templateId 
                }
            });
        },
        onSuccess: (res) => {
            setAlert('URL do WhatsApp gerada com sucesso!', 'success');
            
            if (res.data.data?.urlWhatsApp) {
                window.open(res.data.data.urlWhatsApp, '_blank');
            }
            
            return res.data.data;
        },
        onError: (err) => {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao gerar URL do WhatsApp';
            setAlert(errorMessage, 'danger');
            throw err;
        },
    });

    const isPending = [
        createAgendamento,
        updateAgendamento,
        deleteAgendamento,
        confirmarAgendamento,
        cancelarAgendamento,
        marcarNoShowAgendamento,
        concluirAgendamento,
        checkoutAgendamento,
        atualizarVendaAgendamento,
        reagendarAgendamento,
        gerarPedidoAgendamento,
        gerarVendaPedido,
        enviarMensagemAutomatica,
        enviarMensagemManual,
        obterUrlWhatsApp,
        verificarDisponibilidade,
        verificarConflitos
    ].some(m => m.isPending);

    return { 
        createAgendamento, 
        updateAgendamento, 
        deleteAgendamento, 
        confirmarAgendamento,
        cancelarAgendamento,
        marcarNoShowAgendamento,
        concluirAgendamento,
        checkoutAgendamento,
        atualizarVendaAgendamento,
        reagendarAgendamento,
        gerarPedidoAgendamento,
        gerarVendaPedido,
        enviarMensagemAutomatica,
        enviarMensagemManual,
        obterUrlWhatsApp,
        verificarDisponibilidade, 
        verificarConflitos,
        verificarPacoteDisponivel: useVerificarPacoteDisponivel,
        isPending
    };
};

// ======================================================
// 🎯 AGENDAMENTOS MANAGER (VERSÃO CORRIGIDA)
// ======================================================
export const useAgendamentosManager = (filters = {}, options = {}) => {
    const queryClient = useQueryClient();
    
    // ✅ Garantir que filters não é undefined
    const safeFilters = filters || {};
    
    // ✅ Verificar se tem filtros válidos antes de executar
    const hasValidFilters = useMemo(() => {
        return !!(safeFilters.unidade || 
                  safeFilters.participante || 
                  safeFilters.profissional ||
                  safeFilters['itens.modelo'] ||
                  safeFilters.itens);
    }, [safeFilters.unidade, safeFilters.participante, safeFilters.profissional, safeFilters['itens.modelo'], safeFilters.itens]);
    
    const { 
        data: agendamentosData, 
        isLoading: loadingAgendamentos,
        refetch: refetchAgendamentos,
        isFetched,
        isSuccess,
        error
    } = useAgendamentos(safeFilters, { 
        ...options, 
        enabled: options.enabled !== false && hasValidFilters 
    });
    
    // ✅ Calcular estatísticas a partir dos dados já carregados
    const estatisticas = useMemo(() => {
        const agendamentos = agendamentosData?.data || [];
        return {
            total: agendamentos.length,
            confirmados: agendamentos.filter(a => a.status === 'Confirmado').length,
            pendentes: agendamentos.filter(a => a.status === 'Pendente').length,
            concluidos: agendamentos.filter(a => a.status === 'Concluido').length,
            cancelados: agendamentos.filter(a => a.status === 'Cancelado').length,
        };
    }, [agendamentosData]);
    
    const mutations = useMutateAgendamentos();
    
    const isLoading = loadingAgendamentos;
    
    const refetchAll = () => {
        refetchAgendamentos();
        queryClient.invalidateQueries({ queryKey: [AGENDAMENTOS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: ['vendas'] });
        queryClient.invalidateQueries({ queryKey: ['pedidos'] });
        queryClient.invalidateQueries({ queryKey: ['pacotes-disponiveis'] });
    };
    
    return {
        agendamentos: agendamentosData?.data || [],
        estatisticas,
        isLoading,
        isFetched,
        isSuccess,
        error,
        isLoadingAgendamentos: loadingAgendamentos,
        ...mutations,
        refetchAll
    };
};

// ======================================================
// 🎯 HOOKS PARA RELATÓRIOS E ESTATÍSTICAS
// ======================================================

export const useEstatisticasAgendamentos = (params = {}) => {
    return useQuery({
        queryKey: ['estatisticas-agendamentos', params],
        queryFn: async ({ signal }) => {
            if (signal?.aborted) throw new Error('Query cancelled');
            const { data } = await apiClient.get('/agendamentos/relatorios/estatisticas-gerais', { 
                params,
                signal 
            });
            return data.data;
        },
        enabled: !!params.account || !!params.unidade,
        staleTime: 5 * 60 * 1000,
    });
};

export const useEstatisticasTipoReserva = (params = {}) => {
    return useQuery({
        queryKey: ['estatisticas-tipo-reserva', params],
        queryFn: async ({ signal }) => {
            if (signal?.aborted) throw new Error('Query cancelled');
            const { data } = await apiClient.get('/agendamentos/relatorios/estatisticas-tipo-reserva', { 
                params,
                signal 
            });
            return data.data;
        },
        enabled: !!params.account,
        staleTime: 5 * 60 * 1000,
    });
};

export const useEstatisticasFinanceiras = (params = {}) => {
    return useQuery({
        queryKey: ['estatisticas-financeiras', params],
        queryFn: async ({ signal }) => {
            if (signal?.aborted) throw new Error('Query cancelled');
            const { data } = await apiClient.get('/agendamentos/relatorios/estatisticas-financeiras', { 
                params,
                signal 
            });
            return data.data;
        },
        enabled: !!params.account,
        staleTime: 5 * 60 * 1000,
    });
};

export const useEstatisticasPacotes = (params = {}) => {
    return useQuery({
        queryKey: ['estatisticas-pacotes', params],
        queryFn: async ({ signal }) => {
            if (signal?.aborted) throw new Error('Query cancelled');
            const { data } = await apiClient.get('/agendamentos/relatorios/pacotes-utilizacao', { 
                params,
                signal 
            });
            return data.data;
        },
        enabled: !!params.account,
        staleTime: 5 * 60 * 1000,
    });
};